import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from '../overlays/ModalShell';
import {
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  MapPinIcon,
  BriefcaseIcon,
  IdentificationIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  HeartIcon,
  PencilIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import type { Patient } from '../../services/patientService';

interface ViewPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient | null;
  onEdit?: (patient: Patient) => void;
}

/** Insurance label mapping */
const INSURANCE_LABELS: Record<string, string> = {
  CNOPS: 'CNOPS',
  CNSS: 'CNSS',
  FAR: 'FAR',
  CMIM: 'CMIM',
  other: 'Other',
  none: 'None',
};

/** Relationship to head labels */
const RELATIONSHIP_LABELS: Record<string, string> = {
  self: 'Assuré(e)',
  spouse: 'Conjoint(e)',
  child: 'Enfant',
  parent: 'Parent',
  sibling: 'Frère/Sœur',
  grandparent: 'Grand-parent',
  grandchild: 'Petit-enfant',
  other: 'Autre',
};

/** Gender display */
const GENDER_LABELS: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  M: 'Male',
  F: 'Female',
};

/* ── Tiny reusable pieces ── */

function InfoRow({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  value: string | undefined | null;
  accent?: string;
}) {
  const display = value?.trim() || '—';
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className={`mt-0.5 p-1.5 rounded-lg shrink-0 ${accent ?? 'bg-primary/10 text-primary'}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-foreground mt-0.5 break-words">{display}</p>
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 mb-2 mt-1">
      {children}
    </h3>
  );
}

/* ── Main component ── */

const ViewPatientModal: React.FC<ViewPatientModalProps> = ({
  isOpen,
  onClose,
  patient,
  onEdit,
}) => {
  const { t } = useTranslation(['patients', 'common']);

  if (!isOpen || !patient) return null;

  const fullName = `${patient.firstName} ${patient.lastName}`;
  const initials = `${patient.firstName.charAt(0)}${patient.lastName.charAt(0)}`.toUpperCase();
  const genderLabel = GENDER_LABELS[patient.gender] ?? patient.gender ?? '—';
  const insuranceLabel = INSURANCE_LABELS[patient.insurance] ?? patient.insurance ?? '—';

  const age = patient.birthDate
    ? (() => {
        const bd = new Date(patient.birthDate);
        const diff = Date.now() - bd.getTime();
        return `${Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000))} ${t('common:years', 'ans')}`;
      })()
    : null;

  return (
    <ModalShell isOpen={isOpen} onClose={onClose}>
      <div className="bg-card rounded-2xl border border-border w-full max-w-2xl shadow-xl">
        {/* ── Hero header ── */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="h-28 bg-gradient-to-br from-primary/80 via-primary/60 to-emerald-500/50" />

          <div className="absolute top-3 right-3 flex items-center gap-2">
            {onEdit && (
              <button
                onClick={() => { onClose(); onEdit(patient); }}
                className="p-2 rounded-lg bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
                title={t('actions.editPatient')}
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="absolute left-6 -bottom-10 flex items-end gap-4">
            <div className="h-20 w-20 rounded-2xl bg-card border-4 border-card flex items-center justify-center text-2xl font-bold text-primary shadow-lg">
              {initials}
            </div>
          </div>
        </div>

        <div className="pt-14 px-6 pb-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-card-foreground leading-tight">{fullName}</h2>
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                <span>{genderLabel}</span>
                {age && (
                  <>
                    <span className="text-border">•</span>
                    <span>{age}</span>
                  </>
                )}
                {patient.insurance && patient.insurance !== 'none' && (
                  <>
                    <span className="text-border">•</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700/30">
                      <ShieldCheckIcon className="h-3 w-3" />
                      {insuranceLabel}
                    </span>
                  </>
                )}
              </div>
            </div>

            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border
              ${patient.status === 'active'
                ? 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700/30'
                : patient.status === 'new'
                  ? 'bg-sky-100 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-700/30'
                  : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-700/30'
              }`}
            >
              {t(`common:status.${patient.status}`)}
            </span>
          </div>
        </div>

        <div className="mx-6 border-t border-border" />

        <div className="px-6 py-5 space-y-6">
          <div>
            <SectionHeader>{t('modal.sections.contact', 'Contact')}</SectionHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <InfoRow icon={EnvelopeIcon} label={t('modal.fields.email')} value={patient.email} />
              <InfoRow icon={PhoneIcon} label={t('modal.fields.phone')} value={patient.phone} />
              <InfoRow
                icon={ChatBubbleLeftEllipsisIcon}
                label={t('modal.fields.whatsappPhone', 'WhatsApp')}
                value={patient.whatsappPhone}
                accent="bg-green-500/10 text-green-600 dark:text-green-400"
              />
              <InfoRow icon={MapPinIcon} label={t('modal.fields.address')} value={patient.address} />
            </div>
          </div>

          <div>
            <SectionHeader>{t('modal.sections.personal', 'Personal')}</SectionHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <InfoRow icon={CalendarIcon} label={t('modal.fields.birthDate')} value={patient.birthDate} />
              <InfoRow icon={UserIcon} label={t('modal.fields.gender', 'Gender')} value={genderLabel} />
              <InfoRow icon={BriefcaseIcon} label={t('modal.fields.profession')} value={patient.profession} />
              <InfoRow icon={IdentificationIcon} label={t('modal.fields.cinNumber')} value={patient.cinNumber} />
              <InfoRow icon={UserGroupIcon} label={t('modal.fields.referredBy')} value={patient.referredBy} />
            </div>
          </div>

          <div>
            <SectionHeader>{t('modal.sections.insurance', 'Insurance')}</SectionHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <InfoRow
                icon={ShieldCheckIcon}
                label={t('modal.fields.insurance')}
                value={insuranceLabel}
                accent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              />
              <InfoRow
                icon={UserGroupIcon}
                label="Lien de parenté"
                value={RELATIONSHIP_LABELS[patient.relationshipToHead || 'self'] || patient.relationshipToHead || '—'}
                accent="bg-violet-500/10 text-violet-600 dark:text-violet-400"
              />
            </div>
          </div>

          {(patient.medicalHistoryAllergies || patient.medicalHistoryDiseases || patient.medicalHistoryMedications
            || patient.medical_history_allergies || patient.medical_history_diseases || patient.medical_history_medications) && (
            <div>
              <SectionHeader>{t('modal.sections.medicalHistory', 'Medical History')}</SectionHeader>
              <div className="space-y-1">
                <InfoRow
                  icon={HeartIcon}
                  label={t('modal.fields.medicalHistoryAllergies', 'Allergies')}
                  value={patient.medicalHistoryAllergies || patient.medical_history_allergies}
                  accent="bg-red-500/10 text-red-500 dark:text-red-400"
                />
                <InfoRow
                  icon={HeartIcon}
                  label={t('modal.fields.medicalHistoryDiseases', 'Diseases')}
                  value={patient.medicalHistoryDiseases || patient.medical_history_diseases}
                  accent="bg-amber-500/10 text-amber-600 dark:text-amber-400"
                />
                <InfoRow
                  icon={HeartIcon}
                  label={t('modal.fields.medicalHistoryMedications', 'Medications')}
                  value={patient.medicalHistoryMedications || patient.medical_history_medications}
                  accent="bg-blue-500/10 text-blue-600 dark:text-blue-400"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-muted-foreground/60 pt-2 border-t border-border">
            <span>ID: {patient.id.slice(-12)}</span>
            <span>
              {t('common:lastVisit', 'Last visit')}: {new Date(patient.lastVisit).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">
            {t('common:close', 'Close')}
          </button>
          {onEdit && (
            <button
              onClick={() => { onClose(); onEdit(patient); }}
              className="btn-primary"
            >
              <PencilIcon className="h-4 w-4" />
              <span>{t('actions.editPatient')}</span>
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default ViewPatientModal;
